/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import classNames from "clsx";

import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import { useApi } from "@/hooks/useApi";
import { getAssetUrl } from "@/utils/getAssetUrl";
import { transformData } from "@/utils/transformData";
import { toAbsoluteUrl } from "@/../_metronic/helpers";
import { useThemeMode } from "@/../_metronic/partials";
import { bloodTypes } from "@/utils/options/bloodTypes";
import { birthTypes } from "@/utils/options/birthTypes";
import { useWindowDimensions } from "@/hooks/useWindowDimensions";
import { nursingAdmissionMap } from "@/utils/mappings/nursingAdmission";

import { SelectionType, typeRenderers } from "@/components/HumanBodySelectionTypesMenu";
import { FormattedFormControl } from "@/components/FormControl/FormattedFormControl";
import { TagHTMLOrSkeleton } from "@/components/TagHTMLOrSkeleton";
import { FormGroupRadio } from "@/components/Radio/FormGroupRadio";
import { SkeletonWrapper } from "@/components/SkeletonWrapper";
import { Viewpoint, Point } from "@/components/Viewpoint";
import { FormRadio } from "@/components/Radio/FormRadio";
import { ReactSelect } from "@/components/Selects/ReactSelect";
import { VitalSigns } from "@/components/VitalSigns";

type Props = {
	attendance: Attendance;
};

export function NursingAdmissionView({ attendance }: Props) {
	const [selectedExams, setSelectedExams] = useState<ReactSelectOption[]>([]);
	const [skeleton, setSkeleton] = useState(true);
	const [frontPoints, setFrontPoints] = useState<Point[]>([]);
	const [backPoints, setBackPoints] = useState<Point[]>([]);
	const [hasPregnant, setHasPregnant] = useState(false);
	const [nursingAdmission, setNursingAdmission] = useState<NursingAdmission>(
		{} as NursingAdmission
	);

	const { api } = useApi();
	const { mode } = useThemeMode();
	const { width } = useWindowDimensions();

	const fallbackUserInfo = {} as UserIdAndName;
	const user = nursingAdmission.user || fallbackUserInfo;

	const patientAllergies = nursingAdmission?.patient?.allergies;
	const performedPregnancyExaminations = nursingAdmission?.performedPregnancyExaminations;
	const pregnantPersonDiseaseHistory = nursingAdmission?.pregnantPersonDiseaseHistory;
	const prenatalLocationType = nursingAdmission?.prenatalLocationType;

	async function getNursingAdmissionData() {
		try {
			const { data } = await api.get(`/surgical-center/${attendance.id}/nursing-admission`);

			const transformedData = transformData<NursingAdmission>(data, nursingAdmissionMap);

			const selectedExamsOptions = transformedData.exams?.map((exam: Procedure) => ({
				value: exam.code,
				label: `${exam.name} - ${exam.code}`,
			}));

			const demarcations = transformedData.demarcations;

			const getPoint = (demarcation: Demarcation): Point => ({
				content: demarcation.content,
				x: Number(demarcation.x),
				y: Number(demarcation.y),
			});

			const newFrontPoints = demarcations
				.filter((demarcation) => demarcation.imageId === 1)
				.map(getPoint);

			setFrontPoints(newFrontPoints);

			const newBackPoints = demarcations
				.filter((demarcation) => demarcation.imageId === 2)
				.map(getPoint);

			setBackPoints(newBackPoints);

			const expertise = attendance.surgicalAttendance?.expertise;
			const isPregnant = expertise?.pregnant;

			setHasPregnant(isPregnant);
			setSelectedExams(selectedExamsOptions);
			setNursingAdmission(transformedData);
		} finally {
			setSkeleton(false);
		}
	}

	const birthType = birthTypes.find(
		(birthType) => birthType.value === nursingAdmission?.birthType
	);

	const fatherSanguineType = bloodTypes.find(
		(bloodType) => bloodType.value === nursingAdmission?.fatherSanguineType
	);

	useEffect(() => {
		if (attendance.id) {
			getNursingAdmissionData();
		}
	}, [attendance.id]);

	return (
		<>
			<div className="px-5 pb-7">
				<TagHTMLOrSkeleton
					text="Avaliação pré-operatória"
					tag="p"
					className="font-weight-bold h6 mt-3 mb-6"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<Row className="mb-3">
					<Col xs={6}>
						<TagHTMLOrSkeleton
							text="Médico Resp.:"
							tag="label"
							className="form-label mb-2"
							htmlFor="user-id"
							skeleton={skeleton}
						/>

						<ReactSelect
							isSolid
							isDisabled
							inputId="user-id"
							skeleton={skeleton}
							value={{ value: user.id, label: user.name }}
						/>
					</Col>

					<Col lg={4} xs={6}>
						<FormattedFormControl
							formattedFormControlClassName="form-control-solid"
							type="datetime-local"
							label="Data do Atendimento:"
							id="attendance-date"
							name="attendance_date"
							skeleton={skeleton}
							value={nursingAdmission.attendanceDate}
							readOnly
						/>
					</Col>
				</Row>

				<Row className="mb-3 align-items-center">
					<Col lg={2} xs={6}>
						<TagHTMLOrSkeleton
							text="Início Jejum:"
							tag="label"
							className="form-label m-0"
							htmlFor="fasting-start"
							skeleton={skeleton}
						/>
					</Col>

					<Col lg={4} xs={6}>
						<FormattedFormControl
							type="datetime-local"
							formattedFormControlClassName="form-control-solid"
							id="fasting-start"
							name="fasting_since"
							skeleton={skeleton}
							value={nursingAdmission.fastingSince}
							readOnly
						/>
					</Col>
				</Row>

				<Row className="mb-3 align-items-center">
					<Col sm={7} className="d-flex flex-center justify-content-between">
						<FormGroupRadio
							label="Cirurgia anterior:"
							name="had_previous_surgery"
							requestedValue={nursingAdmission.hadPreviousSurgery}
							skeleton={skeleton}
							containerClassName="flex-grow-1"
							readOnly
							inline
						>
							<FormRadio label="Sim" value="1" id="had-previous-surgery" />
							<FormRadio label="Não" value="0" id="did-not-have-previous-surgery" />
						</FormGroupRadio>
					</Col>

					{Number(nursingAdmission.hadPreviousSurgery) === 1 && (
						<Col
							sm={5}
							className="d-flex flex-grow-1 justify-content-end align-items-center gap-3"
						>
							<Form.Label className="m-0" htmlFor="previous-surgery-description">
								Qual?
							</Form.Label>

							<FormattedFormControl
								formattedFormControlClassName="form-control-solid"
								placeholder="Descreva a cirurgia"
								id="previous-surgery-description"
								name="previous_surgery_description"
								value={nursingAdmission.previousSurgeryDescription}
								readOnly
							/>
						</Col>
					)}
				</Row>
			</div>

			<div className="border-top-dotted border-gray-300 py-3"></div>

			<div className="px-5">
				<TagHTMLOrSkeleton
					tag="p"
					text="Banho Pré-operatório"
					className="font-weight-bold h6"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<Row className="mb-3 border-bottom border-gray-300 py-3 align-items-center">
					<Col sm={7} className="d-flex flex-center justify-content-between">
						<FormGroupRadio
							label="Realizou Banho:"
							name="had_preoperative_bath"
							requestedValue={nursingAdmission.hadPreoperativeBath}
							skeleton={skeleton}
							containerClassName="flex-grow-1"
							readOnly
							inline
						>
							<FormRadio label="Sim" value="1" id="had-preoperative-bath" />
							<FormRadio label="Não" value="0" id="did-not-have-preoperative-bath" />
						</FormGroupRadio>
					</Col>

					{Number(nursingAdmission.hadPreoperativeBath) === 1 && (
						<Col
							sm={5}
							className="d-flex flex-grow-1 justify-content-end align-items-center gap-3"
						>
							<Form.Label className="m-0" htmlFor="preoperative-bath-at">
								Data:
							</Form.Label>

							<FormattedFormControl
								id="preoperative-bath-at"
								type="datetime-local"
								name="preoperative_bath_at"
								formattedFormControlClassName="w-200px form-control-solid"
								value={nursingAdmission.preoperativeBathAt}
								readOnly
							/>
						</Col>
					)}
				</Row>

				<Row className="mb-3 border-bottom border-gray-300 py-3 align-items-center">
					<Col sm={7} className="d-flex flex-center justify-content-between">
						<FormGroupRadio
							label="Retirada de adornos / órteses / próteses:"
							name="had_prothesis_removal"
							requestedValue={nursingAdmission.hadProthesisRemoval}
							skeleton={skeleton}
							containerClassName="flex-grow-1"
							readOnly
							inline
						>
							<FormRadio
								label="Sim"
								value="1"
								id="performed-the-removal-of-the-prosthesis"
							/>

							<FormRadio
								label="Não"
								value="0"
								id="did-not-perform-the-removal-of-the-prosthesis"
							/>
						</FormGroupRadio>
					</Col>

					{Number(nursingAdmission.hadProthesisRemoval) === 1 && (
						<Col
							sm={5}
							className="d-flex flex-grow-1 justify-content-end align-items-center gap-3"
						>
							<Form.Label className="m-0" htmlFor="prothesis-removal-description">
								Qual?
							</Form.Label>

							<FormattedFormControl
								formattedFormControlClassName="form-control-solid"
								id="prothesis-removal-description"
								type="text"
								placeholder="Descreva"
								name="prothesis_removal_description"
								value={nursingAdmission.prothesisRemovalDescription}
								readOnly
							/>
						</Col>
					)}
				</Row>

				<Row className="mb-3 border-bottom border-gray-300 align-items-center py-3">
					<Col sm={7} className="d-flex flex-center justify-content-between">
						<FormGroupRadio
							label="Realizou Tricotomia:"
							name="had_trichotomy"
							requestedValue={nursingAdmission.hadTrichotomy}
							skeleton={skeleton}
							containerClassName="flex-grow-1"
							readOnly
							inline
						>
							<FormRadio label="Sim" value="1" id="performed-trichotomy" />
							<FormRadio label="Não" value="0" id="did-not-perform-trichotomy" />
						</FormGroupRadio>
					</Col>

					{Number(nursingAdmission.hadTrichotomy) === 1 && (
						<Col
							sm={5}
							className="d-flex flex-grow-1 justify-content-end align-items-center gap-3"
						>
							<Form.Label className="m-0" htmlFor="trichotomy-at">
								Data:
							</Form.Label>

							<FormattedFormControl
								type="datetime-local"
								id="trichotomy-at"
								name="trichotomy_at"
								formattedFormControlClassName="w-200px form-control-solid"
								value={nursingAdmission.trichotomyAt}
								readOnly
							/>
						</Col>
					)}
				</Row>

				<Row className="mb-5 align-items-center">
					<Col sm={7} className="d-flex flex-center justify-content-between">
						<FormGroupRadio
							label="Reconciliação medicamentosa:"
							name="had_medical_reconciliation"
							requestedValue={nursingAdmission.hadMedicalReconciliation}
							skeleton={skeleton}
							containerClassName="flex-grow-1"
							readOnly
							inline
						>
							<FormRadio label="Sim" value="1" id="had-medical-reconciliation" />

							<FormRadio
								label="Não"
								value="0"
								id="did-not-have-medical-reconciliation"
							/>
						</FormGroupRadio>
					</Col>
				</Row>
			</div>

			<div className="border-top-dotted border-gray-300 py-3"></div>

			<div className="px-5">
				<TagHTMLOrSkeleton
					tag="p"
					text="Dados Pré - Operatório"
					className="font-weight-bold h6"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<Row className="mb-3 border-bottom border-gray-300 align-items-center py-3">
					<Col sm={7} className="d-flex flex-center justify-content-between">
						<FormGroupRadio
							label="SVD:"
							name="had_svd"
							requestedValue={nursingAdmission.hadSvd}
							skeleton={skeleton}
							containerClassName="flex-grow-1"
							readOnly
							inline
						>
							<FormRadio label="Sim" value="1" id="had-svd" />
							<FormRadio label="Não" value="0" id="did-not-have-svd" />
						</FormGroupRadio>
					</Col>
				</Row>

				<Row className="mb-3 border-bottom border-gray-300 align-items-center py-3">
					<Col sm={7} className="d-flex flex-center justify-content-between">
						<FormGroupRadio
							label="Esvaziamento espontâneo de bexiga:"
							name="spontaneous_bladder_emptying"
							requestedValue={nursingAdmission.spontaneousBladderEmptying}
							skeleton={skeleton}
							containerClassName="flex-grow-1"
							readOnly
							inline
						>
							<FormRadio
								label="Sim"
								value="1"
								id="performed-spontaneous-bladder-emptying"
							/>

							<FormRadio
								label="Não"
								value="0"
								id="not-performed-spontaneous-bladder-emptying"
							/>
						</FormGroupRadio>
					</Col>
				</Row>

				<Row className="mb-3 border-bottom border-gray-300 py-3 align-items-center">
					<Col sm={7} className="d-flex flex-center justify-content-between">
						<FormGroupRadio
							label="Acesso Venoso:"
							name="had_venous_access"
							requestedValue={nursingAdmission.hadVenousAccess}
							skeleton={skeleton}
							containerClassName="flex-grow-1"
							readOnly
							inline
						>
							<FormRadio label="Sim" value="1" id="had-venous-access" />
							<FormRadio label="Não" value="0" id="did-not-see-venous-access" />
						</FormGroupRadio>
					</Col>

					{Number(nursingAdmission.hadVenousAccess) === 1 && (
						<Col
							sm={5}
							className="d-flex flex-grow-1 justify-content-end align-items-center gap-3"
						>
							<Form.Label className="m-0" htmlFor="venous-access-description">
								Qual?
							</Form.Label>

							<FormattedFormControl
								formattedFormControlClassName="form-control-solid"
								id="venous-access-description"
								type="text"
								placeholder="Descreva"
								name="venous_access_description"
								value={nursingAdmission.venousAccessDescription}
								readOnly
							/>
						</Col>
					)}
				</Row>

				<Row className="mb-3 border-bottom border-gray-300 py-3 align-items-center">
					<Col sm={7} className="d-flex flex-center justify-content-between">
						<FormGroupRadio
							label="Pré-anestésico administrado:"
							name="pre_anesthetic_administered"
							requestedValue={nursingAdmission.preAnestheticAdministered}
							skeleton={skeleton}
							containerClassName="flex-grow-1"
							readOnly
							inline
						>
							<FormRadio label="Sim" value="1" id="pre-anesthetic-administered" />
							<FormRadio label="Não" value="0" id="pre-anesthetic-not-administered" />
						</FormGroupRadio>
					</Col>

					{Number(nursingAdmission.preAnestheticAdministered) === 1 && (
						<Col
							sm={5}
							className="d-flex flex-grow-1 justify-content-end align-items-center gap-3"
						>
							<Form.Label
								className="m-0"
								htmlFor="pre-anesthetic-administered-description"
							>
								Qual?
							</Form.Label>

							<FormattedFormControl
								formattedFormControlClassName="form-control-solid"
								id="pre-anesthetic-administered-description"
								type="text"
								placeholder="Descreva"
								name="pre_anesthetic_administered_description"
								value={nursingAdmission.preAnestheticAdministeredDescription}
								readOnly
							/>
						</Col>
					)}
				</Row>

				<Row className="align-items-center mb-5 pt-3">
					<Col lg={5} xs={3}>
						<TagHTMLOrSkeleton
							text="Sorologia:"
							tag="label"
							className="form-label my-2"
							skeleton={skeleton}
						/>
					</Col>

					{skeleton ? (
						<Col lg={7}>
							<SkeletonWrapper width="26%" />
						</Col>
					) : (
						<>
							<Col md="auto" xs={2}>
								<Form.Check
									label="HIV"
									id="hiv"
									name="serology"
									value="HIV"
									readOnly
									checked={nursingAdmission.serology.includes("HIV")}
									disabled
								/>
							</Col>

							<Col md="auto" xs={2}>
								<Form.Check
									label="Sífilis"
									id="siphilis"
									name="serology"
									value="Sífilis"
									readOnly
									checked={nursingAdmission.serology.includes("Sífilis")}
									disabled
								/>
							</Col>

							<Col md="auto" xs={2}>
								<Form.Check
									label="HB"
									id="hb"
									name="serology"
									value="HB"
									readOnly
									checked={nursingAdmission.serology.includes("HB")}
									disabled
								/>
							</Col>

							<Col md="auto" xs={2}>
								<Form.Check
									label="HC"
									id="hc"
									name="serology"
									value="HC"
									readOnly
									checked={nursingAdmission.serology.includes("HC")}
									disabled
								/>
							</Col>
						</>
					)}
				</Row>
			</div>

			<div className="border-top-dotted border-gray-300 py-3"></div>

			<div className="px-5">
				<TagHTMLOrSkeleton
					tag="p"
					text="Demarcação cirúrgica"
					className="font-weight-bold h6 mb-3"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<Col lg={3} md={6}>
					<FormGroupRadio
						name="had_surgical_demarcation"
						requestedValue={nursingAdmission.hadSurgicalDemarcation}
						skeleton={skeleton}
						readOnly
						inline
					>
						<FormRadio label="Sim" value="1" id="had-surgical-demarcation" />
						<FormRadio label="Não" value="0" id="did-not-have-surgical-demarcation" />
					</FormGroupRadio>
				</Col>

				{Number(nursingAdmission.hadSurgicalDemarcation) === 1 && (
					<Row className="mb-5">
						<Col sm={12} md={4}>
							<Viewpoint
								src={toAbsoluteUrl(`/media/images/human-body-front-${mode}.svg`)}
								points={frontPoints}
								onChangePoints={setFrontPoints}
								renderPointContent={(point) => (
									<i className="text-primary">
										{typeRenderers[point.content as SelectionType]}
									</i>
								)}
							/>
						</Col>

						<Col sm={12} md={4}>
							<Viewpoint
								src={getAssetUrl(`/media/images/human-body-back-${mode}.svg`)}
								points={backPoints}
								onChangePoints={setBackPoints}
								renderPointContent={(point) => (
									<i className="text-primary">
										{typeRenderers[point.content as SelectionType]}
									</i>
								)}
							/>
						</Col>
					</Row>
				)}
			</div>

			<div className="border-top-dotted border-gray-300 py-3"></div>

			<div className="px-5 mb-5">
				<TagHTMLOrSkeleton
					text="Exames pré-operatórios"
					tag="label"
					className="font-weight-bold h6"
					htmlFor="preoperative-exams"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<Col lg={10} md={12}>
					<ReactSelect
						inputId="preoperative-exams"
						name="exams"
						value={selectedExams}
						isAsync
						isMulti
						isSolid
						isDisabled
						skeleton={skeleton}
					/>
				</Col>
			</div>

			<div className="border-top-dotted border-gray-300 py-4"></div>

			<Row className="mb-5 px-5">
				<Col sm={6} xs={12} className={classNames(width <= 575 && "mb-3")}>
					<FormGroupRadio
						name="terms_of_consent_signed"
						label="Termos de Consentimento assinados?"
						requestedValue={nursingAdmission.termsOfConsentSigned}
						skeleton={skeleton}
						readOnly
						inline
					>
						<FormRadio label="Sim" value="1" id="has-signed-the-terms" />
						<FormRadio label="Não" value="0" id="not-signed-terms" />
					</FormGroupRadio>

					{Number(nursingAdmission.termsOfConsentSigned) === 0 && (
						<FormattedFormControl
							id="terms-of-consent-not-signed-description"
							placeholder="Descreva"
							name="terms_of_consent_not_signed_description"
							formattedFormControlClassName="mt-3 form-control-solid"
							value={nursingAdmission.termsOfConsentNotSignedDescription}
							readOnly
						/>
					)}
				</Col>

				<Col sm={6} xs={12}>
					<FormGroupRadio
						name="has_personal_history"
						label="Antecedentes pessoais"
						requestedValue={nursingAdmission.hasPersonalHistory}
						skeleton={skeleton}
						readOnly
						inline
					>
						<FormRadio label="Sim" value="1" id="has-personal-history" />
						<FormRadio label="Não" value="0" id="does-not-have-personal-history" />
					</FormGroupRadio>

					{Number(nursingAdmission.hasPersonalHistory) === 1 && (
						<FormattedFormControl
							formattedFormControlClassName="form-control-solid mt-3"
							id="personal-history-description"
							placeholder="Descreva"
							name="personal_history_description"
							value={nursingAdmission.personalHistoryDescription}
							readOnly
						/>
					)}
				</Col>
			</Row>

			<div className="border-top-dotted border-gray-300 py-4"></div>

			<Row className="mb-5 px-5">
				<Col>
					<FormGroupRadio
						name="had_decubitus_restrictions"
						label="Restrições de decúbito"
						requestedValue={nursingAdmission.hadDecubitusRestrictions}
						skeleton={skeleton}
						readOnly
						inline
					>
						<FormRadio label="Sim" value="1" id="had-decubitus-restrictions" />
						<FormRadio label="Não" value="0" id="did-not-have-decubitus-restrictions" />
					</FormGroupRadio>

					{Number(nursingAdmission.hadDecubitusRestrictions) === 1 && (
						<FormattedFormControl
							formattedFormControlClassName="form-control-solid mt-3"
							id="decubitus-restrictions-descriptions"
							placeholder="Descreva"
							name="decubitus_restrictions_descriptions"
							value={nursingAdmission.decubitusRestrictionsDescriptions}
							readOnly
						/>
					)}
				</Col>

				<Col>
					<TagHTMLOrSkeleton
						text="Alergias"
						tag="p"
						className="mb-3 font-weight-bold h6"
						skeleton={skeleton}
					/>

					<div className="d-flex align-items-center gap-3">
						{patientAllergies?.length > 0 &&
							patientAllergies?.map((allergy: Allergies) => (
								<span key={allergy.id}>{allergy.description}</span>
							))}
					</div>
				</Col>
			</Row>

			<div className="border-top-dotted border-gray-300 py-3"></div>

			<div className="px-5">
				<Row className="mb-5">
					<TagHTMLOrSkeleton
						tag="p"
						text="Sinais Vitais"
						className="font-weight-bold h6 mb-5"
						skeleton={skeleton}
						skeletonWidth="20%"
					/>

					<Col lg={4} xs={6}>
						<VitalSigns.BloodPressure
							systolicInputProps={{
								name: "metrics.systolic_blood_pressure",
								value: String(nursingAdmission.metrics?.systolicBloodPressure),
								readOnly: true,
							}}
							diastolicInputProps={{
								name: "metrics.diastolic_blood_pressure",
								value: String(nursingAdmission.metrics?.diastolicBloodPressure),
								readOnly: true,
							}}
							skeleton={skeleton}
						/>
					</Col>

					<Col lg={4} xs={6}>
						<VitalSigns.RespirationFrequency
							id="respiration-frequency"
							value={String(nursingAdmission.metrics?.respiratoryFrequency)}
							name="metrics.respiratory_frequency"
							skeleton={skeleton}
							readOnly
						/>
					</Col>

					<Col lg={4} xs={6}>
						<VitalSigns.OxygenSaturation
							id="oxygen-saturation"
							value={String(nursingAdmission.metrics?.oxygenSaturation)}
							name="metrics.oxygen_saturation"
							skeleton={skeleton}
							readOnly
						/>
					</Col>

					<Col lg={4} xs={6}>
						<VitalSigns.HeartRate
							id="heart-rate"
							value={String(nursingAdmission.metrics?.heartRate)}
							name="metrics.heart_rate"
							skeleton={skeleton}
							readOnly
						/>
					</Col>

					<Col lg={4} xs={6}>
						<VitalSigns.Temperature
							id="temperature"
							value={String(nursingAdmission.metrics?.temperature)}
							skeleton={skeleton}
							readOnly
						/>
					</Col>
				</Row>

				<Col xs={12} className="mb-5">
					<div className="d-flex align-items-center gap-5">
						<FormGroupRadio
							label="Escala de dor:"
							labelClassName="m-0 mt-2"
							name="metrics.pain_scale"
							requestedValue={nursingAdmission.metrics?.painScale}
							skeleton={skeleton}
							containerClassName="flex-grow-1"
							readOnly
							inline
						>
							<FormRadio label="1" value="1" id="one" />
							<FormRadio label="2" value="2" id="two" />
							<FormRadio label="3" value="3" id="three" />
							<FormRadio label="4" value="4" id="four" />
							<FormRadio label="5" value="5" id="five" />
							<FormRadio label="6" value="6" id="six" />
							<FormRadio label="7" value="7" id="seven" />
							<FormRadio label="8" value="8" id="eight" />
							<FormRadio label="9" value="9" id="nine" />
							<FormRadio label="10" value="10" id="ten" />
						</FormGroupRadio>
					</div>
				</Col>
			</div>

			{hasPregnant && (
				<>
					<div className="border-top-dotted border-gray-300 py-3"></div>

					<div className="mb-5 px-5">
						<p className="font-weight-bold h6 mb-5">Histórico Gestacional</p>
						<p className="font-weight-bold h7">Partos Anteriores</p>

						<Row className="align-items-center mb-3">
							<Col xs={2}>
								<Form.Label
									htmlFor="previous-normal-childbirth-count"
									className="m-0"
								>
									Normais:
								</Form.Label>
							</Col>

							<Col xs={2}>
								<FormattedFormControl
									formattedFormControlClassName="form-control-solid"
									isMaskedInput
									mask="00"
									id="previous-normal-childbirth-count"
									value={String(nursingAdmission.previousNormalChildbirthCount)}
									name="previous_normal_childbirth_count"
									readOnly
								/>
							</Col>

							<Col xs={2}>
								<Form.Label
									className="m-0"
									htmlFor="previous-cesarean-delivery-count"
								>
									Cesárea:
								</Form.Label>
							</Col>

							<Col xs={2}>
								<FormattedFormControl
									formattedFormControlClassName="form-control-solid"
									isMaskedInput
									mask="00"
									id="previous-cesarean-delivery-count"
									value={String(nursingAdmission.previousCesareanDeliveryCount)}
									name="previous_cesarean_delivery_count"
									readOnly
								/>
							</Col>

							<Col xs={2}>
								<Form.Label
									className="m-0"
									htmlFor="previous-partial-abortions-count"
								>
									Abortos:
								</Form.Label>
							</Col>

							<Col xs={2}>
								<FormattedFormControl
									formattedFormControlClassName="form-control-solid"
									isMaskedInput
									mask="00"
									id="previous-partial-abortions-count"
									value={String(nursingAdmission.previousPartialAbortionsCount)}
									name="previous_partial_abortions_count"
									readOnly
								/>
							</Col>
						</Row>

						<Row className="align-items-center">
							<Col xs={2}>
								<Form.Label
									className="m-0"
									htmlFor="previous-premature-delivery-count"
								>
									Prematuros:
								</Form.Label>
							</Col>

							<Col xs={2}>
								<FormattedFormControl
									formattedFormControlClassName="form-control-solid"
									isMaskedInput
									mask="00"
									id="previous-premature-delivery-count"
									value={String(nursingAdmission.previousPrematureDeliveryCount)}
									name="previous_premature_delivery_count"
									readOnly
								/>
							</Col>

							<Col xs={2}>
								<Form.Label
									className="m-0"
									htmlFor="previous-instrumental-delivery-count"
								>
									Instrumentais:
								</Form.Label>
							</Col>

							<Col xs={2}>
								<FormattedFormControl
									formattedFormControlClassName="form-control-solid"
									isMaskedInput
									mask="00"
									id="previous-instrumental-delivery-count"
									value={String(
										nursingAdmission.previousInstrumentalDeliveryCount
									)}
									name="previous_instrumental_delivery_count"
									readOnly
								/>
							</Col>

							<Col xs={2}>
								<Form.Label className="m-0" htmlFor="previous-twin-births-count">
									Gemelares:
								</Form.Label>
							</Col>

							<Col xs={2}>
								<FormattedFormControl
									formattedFormControlClassName="form-control-solid"
									isMaskedInput
									mask="00"
									id="previous-twin-births-count"
									value={String(nursingAdmission.previousTwinBirthsCount)}
									name="previous_twin_births_count"
									readOnly
								/>
							</Col>
						</Row>
					</div>

					<div className="border-top-dotted border-gray-300 py-3"></div>

					<div className="px-5">
						<Row className="mb-3 align-items-center">
							<Col md={2} xs={4}>
								<Form.Label
									className="font-weight-bold h6 m-0"
									htmlFor="last-childbirth-at"
								>
									Último Parto
								</Form.Label>
							</Col>

							<Col md={3} xs={6}>
								<FormattedFormControl
									type="datetime-local"
									id="last-childbirth-at"
									name="last_childbirth_at"
									formattedFormControlClassName="form-control-solid"
									value={nursingAdmission.lastChildbirthAt}
									readOnly
								/>
							</Col>
						</Row>

						<Row className="align-items-center">
							<Col md={2} xs={4} className="mb-3">
								<Form.Label className="m-0" htmlFor="birth-type">
									Tipo de Parto
								</Form.Label>
							</Col>

							<Col md={3} xs={6} className="mb-3">
								<ReactSelect
									isSolid
									isDisabled
									inputId="birth-type"
									value={birthType}
									name="birth_type"
								/>
							</Col>

							<Col lg={7} xs={12} className="mb-3">
								<Row>
									<Col sm="auto" xs={4}>
										<Form.Check
											label="Indicação Cesária"
											id="cesarean-section-indication"
											name="cesarean_section_indication"
											readOnly
											checked={Boolean(
												nursingAdmission.cesareanSectionIndication
											)}
											disabled
										/>
									</Col>

									<Col sm="auto" xs={4}>
										<Form.Check
											label="Episiotomia"
											id="had-epsiotomy"
											name="had_epsiotomy"
											readOnly
											checked={Boolean(nursingAdmission.hadEpsiotomy)}
											disabled
										/>
									</Col>

									<Col sm="auto" xs={4}>
										<Form.Check
											label="Uso de Ocitocina"
											id="had-oxytocin-use"
											name="had_oxytocin_use"
											readOnly
											checked={Boolean(nursingAdmission.hadOxytocinUse)}
											disabled
										/>
									</Col>
								</Row>
							</Col>
						</Row>

						<Col xs={12} className="mb-3">
							<div className="d-flex align-items-center gap-20">
								<FormGroupRadio
									label="Local:"
									labelClassName="m-0 mt-2"
									name="birth_place"
									requestedValue={nursingAdmission.birthPlace}
									readOnly
									inline
								>
									<FormRadio
										label="Neste hospital"
										value="1"
										id="in-this-hospital"
									/>

									<FormRadio label="Fora" value="0" id="outside" />
								</FormGroupRadio>
							</div>
						</Col>
					</div>

					<div className="border-top-dotted border-gray-300 py-3"></div>

					<Row className="align-items-center mb-5 px-5">
						<p className="mb-5 font-weight-bold h6">Filhos</p>

						<Col xs={2}>
							<Form.Label htmlFor="children-born-alive" className="m-0">
								Nascidos Vivos
							</Form.Label>
						</Col>

						<Col xs={2}>
							<FormattedFormControl
								formattedFormControlClassName="form-control-solid"
								id="children-born-alive"
								name="children_born_alive"
								value={nursingAdmission.childrenBornAlive}
								readOnly
							/>
						</Col>

						<Col xs={2}>
							<Form.Label htmlFor="children-born-dead" className="m-0">
								Nascidos Mortos
							</Form.Label>
						</Col>

						<Col xs={2}>
							<FormattedFormControl
								formattedFormControlClassName="form-control-solid"
								id="children-born-dead"
								name="children_born_dead"
								value={nursingAdmission.childrenBornDead}
								readOnly
							/>
						</Col>

						<Col xs={2}>
							<Form.Label htmlFor="children-with-malformation" className="m-0">
								Com má formação
							</Form.Label>
						</Col>

						<Col xs={2}>
							<FormattedFormControl
								formattedFormControlClassName="form-control-solid"
								id="children-with-malformation"
								name="children_with_malformation"
								value={nursingAdmission.childrenWithMalformation}
								readOnly
							/>
						</Col>
					</Row>

					<div className="border-top-dotted border-gray-300 py-3"></div>

					<div className="px-5">
						<Row className="align-items-center">
							<p className="mb-5 font-weight-bold h6">Gestação</p>

							<Col lg={3} sm={5} xs={6} className="mb-3">
								<Form.Label className="m-0" htmlFor="last-menstruation-date">
									Data da última menstruação:
								</Form.Label>
							</Col>

							<Col lg={3} md={7} xs={6} className="mb-3">
								<FormattedFormControl
									type="date"
									id="last-menstruation-date"
									name="last_menstruation_date"
									formattedFormControlClassName="form-control-solid"
									value={nursingAdmission.lastMenstruationDate}
									readOnly
								/>
							</Col>

							<Col lg={3} sm={5} xs={6} className="mb-3">
								<Form.Label className="m-0" htmlFor="probable-birthdate">
									Data provável do parto:
								</Form.Label>
							</Col>

							<Col lg={3} md={7} xs={6} className="mb-3">
								<FormattedFormControl
									type="date"
									id="probable-birthdate"
									name="probable_birthdate"
									formattedFormControlClassName="form-control-solid"
									value={nursingAdmission.probableBirthdate}
									readOnly
								/>
							</Col>
						</Row>

						<Row className="align-items-center mb-5">
							<Col md="auto" sm={4} xs={3}>
								<Form.Label className="m-0" htmlFor="gestational-time-weeks">
									Tempo de gestação:
								</Form.Label>
							</Col>

							<Col lg={1} xs={2}>
								<FormattedFormControl
									formattedFormControlClassName="form-control-solid"
									id="gestational-time-weeks"
									name="gestational_time_weeks"
									value={nursingAdmission.gestationalTimeWeeks}
									readOnly
								/>
							</Col>

							<Col md="auto" xs={3}>
								<Form.Label
									className="m-0 ml-2 mr-2"
									htmlFor="gestational-time-weeks"
								>
									semanas e
								</Form.Label>
							</Col>

							<Col lg={1} xs={2}>
								<FormattedFormControl
									formattedFormControlClassName="form-control-solid"
									id="gestational-time-days"
									name="gestational_time_days"
									value={nursingAdmission.gestationalTimeDays}
									readOnly
								/>
							</Col>

							<Col md="auto" xs={1}>
								<Form.Label className="m-0" htmlFor="gestational-time-days">
									dias
								</Form.Label>
							</Col>
						</Row>
					</div>

					<div className="border-top-dotted border-gray-300 py-3"></div>

					<div className="mb-5 px-5">
						<p className="font-weight-bold h6 mb-5">Pré-Natal</p>

						<Row className="align-items-center mb-3">
							<Col
								lg={
									prenatalLocationType === 2 || prenatalLocationType === 3
										? 7
										: 12
								}
								xs={12}
							>
								<div className="d-flex align-items-center gap-13">
									<FormGroupRadio
										label="Realizou pré-natal:"
										labelClassName="m-0 mt-2"
										name="prenatal_location_type"
										requestedValue={prenatalLocationType}
										readOnly
										inline
									>
										<FormRadio label="Não fez" value="1" id="did-not" />

										<FormRadio label="Clínica" value="2" id="clinic" />

										<FormRadio
											label="Posto de saúde"
											value="3"
											id="health post"
										/>
									</FormGroupRadio>
								</div>
							</Col>

							{(Number(prenatalLocationType) === 2 ||
								Number(prenatalLocationType) === 3) && (
								<>
									<Col md={2} xs={5}>
										<Form.Label
											className="m-0"
											htmlFor="prenatal-location-name"
										>
											Nome do local:
										</Form.Label>
									</Col>

									<Col md={3} xs={7}>
										<FormattedFormControl
											formattedFormControlClassName="form-control-solid"
											id="prenatal-location-name"
											name="prenatal_location_name"
											readOnly
											value={nursingAdmission.prenatalLocationName}
										/>
									</Col>
								</>
							)}
						</Row>

						<Row className="align-items-center mb-3">
							<Col sm={2} xs={5}>
								<Form.Label htmlFor="prenatal-card" className="m-0">
									Cartão pré-natal:
								</Form.Label>
							</Col>

							<Col
								lg={5}
								sm={4}
								xs={7}
								className={classNames(width <= 767 && "mb-3")}
							>
								<FormattedFormControl
									id="prenatal-card"
									name="prenatal_card"
									formattedFormControlClassName={classNames(
										"form-control-solid",
										width > 767 && "w-auto"
									)}
									value={nursingAdmission.prenatalCard}
									readOnly
								/>
							</Col>

							<Col sm={2} xs={5}>
								<Form.Label htmlFor="antenatal-doctor" className="m-0">
									Médico pré-natal:
								</Form.Label>
							</Col>

							<Col lg={3} sm={4} xs={7}>
								<FormattedFormControl
									formattedFormControlClassName="form-control-solid"
									id="antenatal-doctor"
									name="antenatal_doctor"
									value={nursingAdmission.antenatalDoctor}
									readOnly
								/>
							</Col>
						</Row>

						<Row className="align-items-center mb-3">
							<Col md={2} xs={5}>
								<Form.Label className="m-0">Exames realizados:</Form.Label>
							</Col>

							<Col
								lg={5}
								md={4}
								xs={7}
								className={classNames(width <= 767 && "mb-3")}
							>
								<Row>
									<Col md="auto" sm={6} xs="auto">
										<Form.Check
											label="Coombs"
											id="coombs"
											name="performed_pregnancy_examinations"
											value="Coombs"
											readOnly
											checked={performedPregnancyExaminations?.includes(
												"Coombs"
											)}
											disabled
										/>
									</Col>

									<Col md="auto" sm={6} xs="auto">
										<Form.Check
											label="RSS"
											id="rss"
											name="performed_pregnancy_examinations"
											value="RSS"
											readOnly
											checked={performedPregnancyExaminations?.includes(
												"RSS"
											)}
											disabled
										/>
									</Col>
								</Row>
							</Col>

							<Col lg={3} sm={4} xs={5}>
								<Form.Label htmlFor="father-sanguine-type" className="m-0">
									Tipo Sanguíneo do Pai:
								</Form.Label>
							</Col>

							<Col lg={2} sm={2} xs={7}>
								<ReactSelect
									isSolid
									isDisabled
									inputId="father-sanguine-type"
									name="father_sanguine_type"
									value={fatherSanguineType}
								/>
							</Col>
						</Row>

						<Row>
							<Col xs="auto" className={classNames(width <= 767 && "mb-3")}>
								<Form.Label>Paciente já teve ou tem:</Form.Label>
							</Col>

							<Col md="auto" sm={9} sx={12}>
								<Row className="align-items-center">
									<Col xs="auto">
										<Form.Check
											label="HIV"
											id="has-hiv"
											name="pregnant_person_disease_history"
											value="HIV"
											readOnly
											checked={pregnantPersonDiseaseHistory?.includes("HIV")}
											disabled
										/>
									</Col>

									<Col xs="auto">
										<Form.Check
											label="Sífilis"
											id="has-syphilis"
											name="pregnant_person_disease_history"
											value="Sífilis"
											readOnly
											checked={pregnantPersonDiseaseHistory?.includes(
												"Sífilis"
											)}
											disabled
										/>
									</Col>

									<Col xs="auto">
										<Form.Check
											label="HB"
											id="has-hb"
											name="pregnant_person_disease_history"
											value="HB"
											readOnly
											checked={pregnantPersonDiseaseHistory?.includes("HB")}
											disabled
										/>
									</Col>

									<Col xs="auto">
										<Form.Check
											label="HC"
											id="has-hc"
											name="pregnant_person_disease_history"
											value="HC"
											readOnly
											checked={pregnantPersonDiseaseHistory?.includes("HC")}
											disabled
										/>
									</Col>

									<Col xs="auto" className={classNames(width <= 384 && "mt-2")}>
										<Form.Check
											label="Covid-19"
											id="has-covid-19"
											name="pregnant_person_disease_history"
											value="Covid-19"
											readOnly
											checked={pregnantPersonDiseaseHistory?.includes(
												"Covid-19"
											)}
											disabled
										/>
									</Col>
								</Row>
							</Col>
						</Row>
					</div>

					<div className="border-top-dotted border-gray-300 py-4"></div>

					<div className="mb-5 px-5">
						<Col lg={10} md={12} className="mb-3">
							<FormattedFormControl
								formattedFormControlClassName="form-control-solid"
								as="textarea"
								id="complications-during_pregnancy"
								label="Complicações durante a gestação"
								placeholder="Descreva"
								name="complications_during_pregnancy"
								value={nursingAdmission.complicationsDuringPregnancy}
								readOnly
							/>
						</Col>

						<Col lg={10} md={12} className="mb-3">
							<FormattedFormControl
								formattedFormControlClassName="form-control-solid"
								as="textarea"
								id="medications-during-pregnancy"
								label="Medicações durante a gestação"
								placeholder="Descreva"
								name="medications_during_pregnancy"
								value={nursingAdmission.medicationsDuringPregnancy}
								readOnly
							/>
						</Col>

						<Col lg={10} md={12} className="mb-3">
							<FormattedFormControl
								formattedFormControlClassName="form-control-solid"
								as="textarea"
								id="pregnancy-remarks"
								label="Outras observações da gestação"
								placeholder="Descreva"
								name="pregnancy_remarks"
								value={nursingAdmission.pregnancyRemarks}
								readOnly
							/>
						</Col>
					</div>
				</>
			)}

			<div className="border-top-dotted border-gray-300 py-4"></div>

			<Col lg={10} md={12} className="mb-3 px-5">
				<FormattedFormControl
					formattedFormControlClassName="form-control-solid"
					as="textarea"
					id="nursing-remarks"
					label="Anotações de enfermagem"
					placeholder="Descreva"
					name="nursing_remarks"
					skeleton={skeleton}
					value={nursingAdmission.nursingRemarks ?? ""}
					readOnly
				/>
			</Col>
		</>
	);
}
